import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { RequestError } from '@/services/config';
import { Http } from '@/services/httpRequest';

import { mapVideoStreamSettingsValuesToDomain } from './mappers';
import type {
    UpdateBioscopeVideoStreamSettingsVariables,
    UseUpdateBioscopeVideoStreamSettingsResult,
    VideoStreamSettingsValues,
} from './types';
import { getClientVideoStreamSettingsKey } from './useGetClientVideoStreamSettings';

export const useUpdateBioscopeVideoStreamSettings =
    (): UseUpdateBioscopeVideoStreamSettingsResult => {
        const queryClient = useQueryClient();
        const mutation = useMutation<
            VideoStreamSettingsValues,
            RequestError | Error,
            UpdateBioscopeVideoStreamSettingsVariables
        >({
            mutationFn: async (variables: UpdateBioscopeVideoStreamSettingsVariables) => {
                const { clientId, bioscopeId, requestBody } = variables;
                const response = await Http.putRequest(
                    `/api/v2/client/internal/${clientId}/bioscope/${bioscopeId}/video-stream-settings`,
                    undefined,
                    requestBody,
                    undefined,
                    {
                        headers: { 'Content-Type': 'application/json' },
                    }
                );

                return mapVideoStreamSettingsValuesToDomain(response.data);
            },
            onSuccess: (_data, variables) => {
                // Do not await refetch inside mutationFn: a refetch error would fail the mutation after a successful PUT.
                void queryClient.invalidateQueries({
                    queryKey: getClientVideoStreamSettingsKey(variables.clientId),
                    exact: true,
                });
            },
        });

        return {
            ...mutation,
            isLoading: mutation.isPending,
            error: mutation.error ?? null,
        };
    };
